import { useEffect, useRef, useMemo } from "react";
import { parseBBCode, countVisibleChars } from "../engine/bbcode";
import { RichText } from "./RichText";

export interface BacklogEntry {
  speaker: string | null;
  text: string;
  speakerColor?: string;
}

interface BacklogProps {
  entries: BacklogEntry[];
  onClose: () => void;
}

export function Backlog({ entries, onClose }: BacklogProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "instant" });
  }, [entries.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0, 0, 0, 0.92)",
        display: "flex",
        flexDirection: "column",
        zIndex: 100,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1rem 2rem",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "1.2rem" }}>Backlog</h2>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "6px",
            color: "#eee",
            padding: "0.4rem 1rem",
            cursor: "pointer",
            fontSize: "0.85rem",
          }}
        >
          Close
        </button>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "1rem 2rem",
        }}
      >
        {entries.length === 0 && (
          <p style={{ opacity: 0.4, textAlign: "center", marginTop: "3rem" }}>
            No dialogue yet.
          </p>
        )}
        {entries.map((entry, i) => (
          <div
            key={i}
            style={{
              padding: "0.6rem 0",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            {entry.speaker && (
              <span
                style={{
                  color: entry.speakerColor ?? "#7ec8e3",
                  fontWeight: "bold",
                  marginRight: "0.5rem",
                  fontSize: "0.9rem",
                }}
              >
                {entry.speaker}
              </span>
            )}
            <span style={{ fontSize: "0.95rem", lineHeight: 1.6 }}>
              <BacklogText text={entry.text} />
            </span>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

function BacklogText({ text }: { text: string }) {
  const nodes = useMemo(() => parseBBCode(text), [text]);
  const total = useMemo(() => countVisibleChars(nodes), [nodes]);
  return <RichText nodes={nodes} revealedCount={total} />;
}
